import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

const NAV = [
  { label: "HOME", to: "/" },
  { label: "PORTFOLIO", to: "/portfolio" },
  { label: "SERVICES", to: "/services" },
  { label: "ABOUT", to: "/about" },
  { label: "CONTACT", to: "/contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 60);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  // Hide nav completely on /admin (has own chrome)
  if (location.pathname.startsWith("/admin")) return null;

  // Pages that start with a light (ivory) background at the top.
  // On those pages the nav should show the BLACK logo + ink text when at top,
  // and switch to WHITE + ivory once the nav gets a dark backdrop on scroll.
  const lightHeroRoutes = [
    /^\/about\/?$/,
    /^\/services\/?$/,
    /^\/contact\/?$/,
    /^\/booking\/?$/,
    /^\/privacy\/?$/,
    /^\/terms\/?$/,
  ];
  const isLightHero = lightHeroRoutes.some((rx) => rx.test(location.pathname));
  const useLightLogo = scrolled || !isLightHero; // white logo when true
  const useLightText = useLightLogo; // matching text colour

  return (
    <>
      <header
        data-testid="site-nav"
        className={`fixed top-0 left-0 right-0 z-40 transition-[background-color,backdrop-filter,border-color,color] duration-500 ${
          scrolled
            ? "bg-[#0a0a0a]/70 backdrop-blur-md border-b border-white/5"
            : "bg-transparent border-b border-transparent"
        } ${useLightText ? "text-ivory" : "text-ink"}`}
      >
        <div className={`mx-auto flex items-center justify-between px-6 md:px-12 lg:px-16 h-16 md:h-20`}>
          <Link to="/" data-testid="nav-logo" className="inline-flex items-center h-8">
            <img
              src={useLightLogo ? "/brand/oereshaus-white.webp" : "/brand/oereshaus-black.webp"}
              alt="Oereshaus"
              className="h-full w-auto object-contain transition-opacity duration-500"
              draggable="false"
            />
          </Link>

          <nav className="hidden md:flex items-center gap-9">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-link-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `label-mono hover-underline ${isActive ? "opacity-100" : "opacity-70 hover:opacity-100"} transition-opacity duration-300`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:block">
            <Link to="/booking" data-testid="nav-cta" className="label-mono hover-underline">
              ENQUIRE
            </Link>
          </div>

          <button
            type="button"
            data-testid="nav-menu-toggle"
            aria-label="Open menu"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden inline-flex items-center justify-center w-9 h-9"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.19, 1, 0.22, 1] }}
            className="fixed inset-0 z-30 bg-ink text-ivory md:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col justify-center items-start px-8 h-full gap-8">
              {NAV.map((item, i) => (
                <motion.div
                  key={item.to}
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.1 + i * 0.06, duration: 0.6 }}
                >
                  <NavLink
                    to={item.to}
                    className="font-editorial text-5xl"
                    data-testid={`mobile-nav-link-${item.label.toLowerCase()}`}
                  >
                    {item.label.charAt(0) + item.label.slice(1).toLowerCase()}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.6 }}
              >
                <Link to="/booking" className="btn-outline-ivory" data-testid="mobile-nav-cta">
                  Enquire
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
